import { formatMoney } from '@/lib/money'

// Accept any action array shape (getById details or listForStore rows).
type Actions = readonly any[] | undefined

export type Discount =
    | { kind: 'percentage'; percent: number }
    | { kind: 'amount'; minor: number; currency: string }
    | { kind: 'freeShipping' }

/**
 * Extracts the first meaningful discount from a promotion's actions.
 * For amount discounts, prefers the store base currency, falling back to the
 * first currency the promotion defines.
 */
export const getPromotionDiscount = (actions: Actions, baseCurrency: string): Discount | null => {
    for (const a of actions ?? []) {
        if (
            'adjustmentType' in a &&
            a.adjustmentType === 'percentage' &&
            'adjustmentPercentage' in a &&
            a.adjustmentPercentage != null
        ) {
            return { kind: 'percentage', percent: a.adjustmentPercentage }
        }
        if ('adjustmentType' in a && a.adjustmentType === 'amount' && 'adjustmentAmount' in a && a.adjustmentAmount) {
            const map = a.adjustmentAmount as Record<string, { amount: number }>
            if (map[baseCurrency]) return { kind: 'amount', minor: map[baseCurrency].amount, currency: baseCurrency }
            const [currency, option] = Object.entries(map)[0] ?? []
            if (currency && option) return { kind: 'amount', minor: option.amount, currency }
        }
        if ('freeShipping' in a && a.freeShipping) return { kind: 'freeShipping' }
    }
    return null
}

/** One short label per action (e.g. "-10%", "-$5.00", "Free shipping", "Free trial", "BOGO"). */
export const getActionLabels = (actions: Actions, baseCurrency: string): string[] => {
    const labels: string[] = []
    for (const a of actions ?? []) {
        if ('adjustmentType' in a && a.adjustmentType === 'percentage' && a.adjustmentPercentage != null) {
            labels.push(`-${a.adjustmentPercentage}%`)
        } else if ('adjustmentType' in a && a.adjustmentType === 'amount' && a.adjustmentAmount) {
            const map = a.adjustmentAmount as Record<string, { amount: number }>
            const opt = map[baseCurrency] ?? Object.values(map)[0]
            const cur = map[baseCurrency] ? baseCurrency : Object.keys(map)[0]
            if (opt) labels.push(`-${formatMoney(opt.amount, cur)}`)
        } else if (a.freeShipping || a.type === 'ShippingAdjustment') {
            labels.push('Free shipping')
        } else if (a.type === 'CreateLineItem') {
            labels.push('Free product')
        } else if (a.type === 'SubscriptionLineItemFreeTrial') {
            labels.push(
                a.subscriptionFreeTrialDuration
                    ? `${a.subscriptionFreeTrialDuration} ${a.subscriptionFreeTrialDurationType ?? 'day'} trial`
                    : 'Free trial',
            )
        } else if (a.type === 'BOGO') {
            labels.push('BOGO')
        }
    }
    return labels
}

/** Short chip label, e.g. "-10%", "-$5.00", "Free shipping". */
export const formatDiscount = (discount: Discount | null): string | null => {
    if (!discount) return null
    if (discount.kind === 'percentage') return `-${discount.percent}%`
    if (discount.kind === 'amount') return `-${formatMoney(discount.minor, discount.currency)}`
    return 'Free shipping'
}

/** Applies a discount to a regular total (minor units). Free shipping leaves the item total unchanged. */
export const applyDiscount = (regularMinor: number, discount: Discount | null): number => {
    if (!discount) return regularMinor
    if (discount.kind === 'percentage') return Math.max(0, Math.round(regularMinor * (1 - discount.percent / 100)))
    if (discount.kind === 'amount') return Math.max(0, regularMinor - discount.minor)
    return regularMinor
}

/**
 * A promotion qualifies as a "VIP discount" when it matches the prefilled VIP
 * configuration: enabled, automatic, force-combine + all combinations on, no
 * usage limit, not once-per-customer, and a ContainsProduct rule on the VIP product.
 */
export const isVipDiscount = (promotion: any, vipProductId: string | null | undefined): boolean => {
    if (!vipProductId || !promotion) return false
    return Boolean(
        promotion.enabled &&
            promotion.automatic &&
            promotion.forceCombine &&
            promotion.combinesWithOrderLevelDiscounts &&
            promotion.combinesWithLineItemDiscounts &&
            promotion.combinesWithShippingDiscounts &&
            !promotion.oncePerCustomer &&
            promotion.usageLimit == null &&
            Array.isArray(promotion.rules) &&
            promotion.rules.some((r: any) => r?.type === 'ContainsProduct' && r?.productId === vipProductId),
    )
}

export interface PromotionTriggerFlags {
    automatic?: boolean | null
    usageCount?: number | null
    archived?: boolean | null
    endDate?: string | Date | null
    hasPromotionCode: boolean
    hasOfferLink: boolean
    hasPromotionTrigger: boolean
}

/**
 * A promotion the merchant has no way of firing: not automatic, and attached to no promo code,
 * no offer and no trigger. The checkout can still apply it when a caller passes its id — offers,
 * upsells, rebills and storefront mirroring all do — so this is a merchant-facing hint, never a
 * validation rule. A promotion that has already been used is therefore excluded: something is
 * evidently triggering it.
 *
 * `automatic` is nullable in Postgres, so NULL must read as false. A promotion carrying a trigger
 * legitimately sits code-less until its first qualifying order mints a code.
 */
export const isPromotionWithoutTrigger = (promotion: PromotionTriggerFlags | null | undefined): boolean => {
    if (!promotion) return false
    return (
        promotion.automatic !== true &&
        !promotion.hasPromotionCode &&
        !promotion.hasOfferLink &&
        !promotion.hasPromotionTrigger &&
        (promotion.usageCount ?? 0) === 0
    )
}

/**
 * Whether the CRM should warn about a promotion having no trigger. Archived and expired
 * promotions are excluded: they already read as such, and adding a promo code would not revive
 * them.
 */
export const shouldWarnPromotionWithoutTrigger = (
    promotion: PromotionTriggerFlags | null | undefined,
): boolean => {
    if (!isPromotionWithoutTrigger(promotion)) return false
    if (promotion!.archived) return false
    const endDate = promotion!.endDate
    return !endDate || new Date(endDate) >= new Date()
}
